'use client'

import type { SvgIconProps } from '@mui/material/SvgIcon'
import { preferredBranches } from '@/constants/dropdown'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { sendGAEvent } from '@next/third-parties/google'
import Image from 'next/image'
import { useRouter } from 'next/navigation'

import { useBranches } from '@/app/GlobalChromeContext'
import arrowndown from '@/assets/images/arrow-down.svg'
import { PrimaryButton } from '@/components/buttons'
import useStore from '@/stores/Store'
import { formatPhoneNumber } from '@/utils/phone'
import { submitReferralForm } from '@/utils/service'

const INIT_FORM_DATA = {
  full_name: '',
  contact_number: '',
  rm_name: '',
  ref_full_name: '',
  ref_contact_number: '',
  ref_email: '',
  ref_preferred_branch: '',
  agree: false,
  timestamp: ''
}

const CustomArrow = ({ className }: SvgIconProps) => {
  return <Image src={arrowndown} alt="" width={18} height={18} className={className} />
}

const ReferralForm = () => {
  const router = useRouter()
  const [_, setIsError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [formData, setFormData] = useState(INIT_FORM_DATA)
  const [isConfirm, setIsConfirm] = useState(false)
  const [validationMessage, setValidationMessage] = useState('')
  const [submitDisabled, setSubmitDisabled] = useState(false)

  const ref = useRef(null)
  const { setFormRef } = useStore()
  const cmsBranches = useBranches()
  const branchOptions =
    cmsBranches.length > 0
      ? cmsBranches.filter(branch => branch.type !== 'ATM').map(branch => branch.name ?? '')
      : preferredBranches

  const handleChange = e => {
    const { name, value, type, checked } = e.target

    if (name === 'contact_number' || name === 'ref_contact_number') {
      const formattedValue = formatPhoneNumber(value)
      setFormData({
        ...formData,
        [name]: formattedValue
      })
    } else if (name === 'full_name' || name === 'rm_name' || name === 'ref_full_name') {
      if (/^[a-zA-Z\s]*$/.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        })
      }
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      })
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    setValidationMessage('')

    if (!formData.full_name) {
      setValidationMessage('Please enter your full name.')
      return
    }
    if (!formData.contact_number) {
      setValidationMessage('Please enter your contact number.')
      return
    }
    if (!formData.rm_name) {
      setValidationMessage("Please enter the Relationship Manager's name.")
      return
    }
    if (!formData.ref_full_name) {
      setValidationMessage('Please enter Referee full name.')
      return
    }
    if (!formData.ref_contact_number) {
      setValidationMessage('Please enter Referee contact number.')
      return
    }
    if (!formData.ref_email) {
      setValidationMessage('Please enter Referee email.')
      return
    }
    if (!formData.ref_preferred_branch) {
      setValidationMessage('Please select a preferred branch.')
      return
    }
    if (!formData.agree) {
      setValidationMessage('Please agree to the terms.')
      return
    }

    setFormData({
      ...formData,
      timestamp: new Date().toISOString()
    })
    setIsConfirm(true)
  }

  const confirm = async () => {
    sendGAEvent({
      event: 'form_submit',
      category: 'Submit referral enquiry form',
      action: `Referral form submitted by ${formData.full_name}`,
      label: window.location.pathname + window.location.search
    })

    setSubmitDisabled(true)
    // Handle form submission logic

    try {
      await submitReferralForm(formData)
      setFormData(INIT_FORM_DATA) // Reset form data after successful submission
      setIsError(false)
      setValidationMessage('') // Clear validation message on success
      setSubmitDisabled(false)
      router.push('/personal/enquiry/thank-you/')
    } catch (error) {
      // The rejection may be a string or an Error; normalise before matching,
      // or `.includes` throws inside the handler and hides the real failure.
      const message = error instanceof Error ? error.message : String(error)

      if (message.includes('ref_email')) {
        setErrorMessage(`Error: Please enter a valid email address.`)
      } else {
        setErrorMessage(`Error: ${message}`)
      }
      setIsError(true)
      setSubmitDisabled(false)
    }
  }

  const checkConditions = useCallback(() => {
    // const isConsentChecked = formData.agree;
    const submitButton = document.getElementById('submitButton') as HTMLButtonElement | null
    if (submitButton) {
      // Check if conditions to show/hide submit button (if necessary)
      // Not disabling the button as per your requirement
      submitButton.disabled = false // Keep the button enabled
    }
  }, [])

  useEffect(() => {
    checkConditions() // Call the function whenever the dependencies change
  }, [checkConditions])

  useEffect(() => {
    setFormRef(ref)
  }, [])

  return (
    <>
      <Modal show={!!errorMessage} aria-label="Submission error">
        <Modal.Body>
          <p>{errorMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setErrorMessage('')
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={isConfirm} aria-label="Confirm submission">
        <Modal.Body>
          <p>Please review your details before submitting.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setIsConfirm(false)
            }}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setIsConfirm(false)
              confirm()
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!validationMessage} aria-label="Form validation">
        <Modal.Body>
          <p>{validationMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setValidationMessage('')
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <div ref={ref} id="referral-form">
        <div className="col-xl-12">
          <h2 className="referral-form__title">Refer a friend or family member today!</h2>
          <p>Share their contact details, and we'll reach out to them personally.</p>
        </div>
        <form className="app-form-landing" onSubmit={handleSubmit}>
          <div className="col row justify-content-between flex-md-nowrap" style={{ gap: '5%' }}>
            <div className="col-md col-12 mb-5">
              <p className="referral-form__column-heading mb-4">Your details</p>
              <div className="form-group col-12 mb-4">
                <label className="form-label" htmlFor="full_name">
                  <span style={{ color: '#FF5800' }}>*</span>
                  <span data-translate="forms_3">Name</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="full_name"
                  name="full_name"
                  placeholder="Enter your full name"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group col-12 mb-4">
                <label className="form-label" htmlFor="phone">
                  <span style={{ color: '#FF5800' }}>*</span>
                  <span data-translate="forms_4">Phone No.</span>
                </label>
                <div className="row">
                  <div className="col-12">
                    <input
                      type="text"
                      name="contact_number"
                      className="form-control"
                      placeholder="012-3456789"
                      maxLength={13}
                      required
                      value={formData.contact_number}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group col-12 mb-4">
                <label htmlFor="rm_name" className="form-label">
                  <span style={{ color: '#FF5800' }}>*</span>
                  <span data-translate="forms_5">Relationship Manager Name</span>
                </label>
                <input
                  type="text"
                  name="rm_name"
                  className="form-control"
                  placeholder="Enter your Relationship Manager Name"
                  id="rm_name"
                  value={formData.rm_name}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="col-md col-12">
              <p className="referral-form__column-heading mb-4">Your referee's details</p>
              <div className="form-group col-12 mb-4">
                <label className="form-label" htmlFor="ref_full_name">
                  <span style={{ color: '#FF5800' }}>*</span>
                  <span data-translate="forms_3">Name</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="ref_full_name"
                  name="ref_full_name"
                  placeholder="Enter your friend/family’s name"
                  required
                  value={formData.ref_full_name}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group col-12 mb-4">
                <label className="form-label" htmlFor="ref_phone">
                  <span style={{ color: '#FF5800' }}>*</span>
                  <span data-translate="forms_4">Phone No.</span>
                </label>
                <div className="row">
                  <div className="col-12">
                    <input
                      type="text"
                      id="ref_phone"
                      name="ref_contact_number"
                      className="form-control"
                      placeholder="012-3456789"
                      maxLength={13}
                      required
                      value={formData.ref_contact_number}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group col-12 mb-4">
                <label htmlFor="email" className="form-label">
                  <span style={{ color: '#FF5800' }}>*</span>
                  <span data-translate="forms_5">Email</span>
                </label>
                <input
                  type="email"
                  name="ref_email"
                  placeholder="youremail@here.com"
                  className="form-control"
                  id="email"
                  value={formData.ref_email}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group col-12 mb-4">
                <label className="form-label" htmlFor="preferred_branch">
                  <span style={{ color: '#FF5800' }}>*</span>
                  <span data-translate="forms_7">Preferred Branch</span>
                </label>
                <FormControl fullWidth>
                  <Select
                    className="custom-mui-select"
                    id="preferred_branch"
                    name="ref_preferred_branch"
                    IconComponent={CustomArrow}
                    value={formData.ref_preferred_branch}
                    displayEmpty
                    onChange={handleChange}
                    MenuProps={{
                      slotProps: { paper: { className: 'custom-mui-dropdown' } }
                    }}
                    renderValue={selected => {
                      if (!selected) {
                        return <span className="placeholder-text">--Select--</span>
                      }
                      return selected
                    }}
                  >
                    <MenuItem value="" disabled>
                      --Select--
                    </MenuItem>

                    {branchOptions.map(branch => (
                      <MenuItem key={branch} value={branch}>
                        {branch}
                      </MenuItem>
                    ))}

                    <MenuItem value="No Preference">
                      I don't have a preferred bank branch or Smart Financial Center
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
          <div className="col-12">
            <div
              className="row flex-nowrap"
              style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}
            >
              <div className="w-auto" style={{ paddingTop: '6px' }}>
                <input
                  type="checkbox"
                  id="consent"
                  name="agree"
                  value="1"
                  required
                  className="referral-consent-checkbox"
                  aria-label="I confirm and consent to the referral program terms"
                  aria-describedby="consent-text"
                  checked={formData.agree}
                  onChange={handleChange}
                />
              </div>
              <div className="col-xl-11 col-11" id="consent-text" style={{ paddingLeft: 0 }}>
                <p style={{ textAlign: 'left', marginBottom: '0.5rem' }} data-translate="forms_12">
                  I hereby confirm that:-
                </p>
                <ol type="a" style={{ paddingLeft: '1.25rem' }}>
                  <li>
                    I have obtained express consent from the individual above (“Referee”) to
                    disclose his/her name and contact details to{' '}
                    <span className="no-wrap">Al Rajhi Banking</span> & Investment Corporation
                    (Malaysia) Bhd (“alrajhi bank Malaysia”).
                  </li>
                  <li>
                    The referee has no objections to alrajhi bank Malaysia contacting him/her for
                    the purposes of this referral program.
                  </li>
                  <li>
                    I have read and agreed to the contents and terms of the Personal Data Protection
                    Notice and our Privacy Policy.
                  </li>
                  <li>
                    I agree and consent for my name to be disclosed to the Referee for the purposes
                    of this referral program.
                  </li>
                </ol>
              </div>
            </div>
            <div className="col-12 col-md-4 d-grid gap-2 mx-auto">
              <PrimaryButton
                text="Submit"
                disabled={submitDisabled}
                onClick={handleSubmit}
                id="submitButton"
              />
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export default ReferralForm
